import { z } from 'zod';
import { ModoPermissaoSchema } from './modos';
import { EsforcoOpencodeSchema, MOTORES, MotorSchema, motorNaoTratado, type Motor } from '@remoto/protocolo';
import { EsforcoSchema, ModeloSchema } from './modelos';

export * from './continuar';
export * from './heranca';
export * from './titulo';
export * from './modelos';
export * from './modos';
export * from './diff';
export * from './carga-do-codex';
export * from './erro-do-motor';
export * from './modelo-trocado';
export * from './uso-de-subagente';
export * from './uso-do-motor';

export * from './eventos';

export const OrigemSessaoSchema = z.enum(['OBSERVADA', 'SDK']);
export type OrigemSessao = z.infer<typeof OrigemSessaoSchema>;

/**
 * NORMAL ou MESTRE. A sessão mestre (F13) é a que conhece a frota inteira:
 * uma só vigente por vez, fixada no topo da lista, com as ferramentas
 * `mcp__remoto__*`. Campo novo entra com `.default()` para o cliente antigo
 * seguir válido.
 */
export const FuncaoSessaoSchema = z.enum(['NORMAL', 'MESTRE']);
export type FuncaoSessao = z.infer<typeof FuncaoSessaoSchema>;

export const EstadoSessaoSchema = z.enum([
  'ATIVA',
  'DORMINDO',
  'AGUARDANDO_PERMISSAO',
  'ENCERRADA',
]);
export type EstadoSessao = z.infer<typeof EstadoSessaoSchema>;

/**
 * A regra de modelo e esforço, que é DIFERENTE em cada motor — e mora numa
 * função porque vale igual em criar sessão, criar mestre e trocar de motor.
 *
 * **CLAUDE**: `modelo` e `esforco` são opcionais e, se vierem, têm de estar nos
 * enums da casa. Opcional porque existe política: faltando, o serviço cai em
 * `MODELO_PADRAO`/`ESFORCO_SESSAO` e a sessão nasce certa.
 *
 * **CODEX**: `modelo` é OBRIGATÓRIO e `esforco` é livre. Obrigatório porque não
 * há política em que cair — o catálogo vem da máquina (`model/list`), e a regra
 * da casa manda `model` e `effort` explícitos em toda sessão; um modelo ausente
 * viraria `undefined` viajando até o app-server. Livre porque `ultra` existe lá
 * (medido em M0-3) e não cabe no `EsforcoSchema`.
 *
 * **OPENCODE** (F16): `modelo` é OBRIGATÓRIO na sessão comum (texto livre, o
 * id do OpenRouter; se ele tem `tools` e imagem é pergunta do catálogo, e quem
 * responde é a API). `esforco`, se vier, tem a forma de `EsforcoOpencodeSchema`
 * — `padrao` ou o nome de um `variant`; se aquele variant existe naquele modelo
 * é, de novo, do catálogo. Na MESTRE os dois são RECUSADOS: o modelo e o
 * esforço da mestre do OpenCode moram na tela de Configurações (D2, D17), e um
 * segundo lugar para escolhê-los seria uma segunda resposta para a mesma
 * pergunta. E nos três gestos a `confirmacaoOpencode` é exigida (D10): o
 * conteúdo sai da rede de casa e a sessão gasta crédito.
 *
 * Os campos são `z.string()` no schema e a checagem acontece aqui, e não o
 * contrário: com `ModeloSchema.optional()` no campo, um modelo do Codex seria
 * recusado ANTES de o motor ser lido, e a mensagem falaria de um enum que não
 * governa aquele motor.
 *
 * `switch` exaustivo: o `if (v.motor === 'CODEX')` que existia aqui mandava o
 * terceiro motor para a política do Claude, e o OpenCode nasceria com `opus`.
 */
function refinarModeloEEsforco(origem: 'sessao' | 'mestre') {
  return (
    v: { motor: Motor; modelo?: string; esforco?: string; confirmacaoOpencode?: boolean },
    ctx: z.RefinementCtx,
  ): void => {
    switch (v.motor) {
      case 'CLAUDE':
        if (v.modelo !== undefined && !ModeloSchema.safeParse(v.modelo).success) {
          ctx.addIssue({
            code: 'custom',
            path: ['modelo'],
            message: 'modelo_desconhecido: fora da politica da casa para o motor CLAUDE',
          });
        }
        if (v.esforco !== undefined && !EsforcoSchema.safeParse(v.esforco).success) {
          ctx.addIssue({
            code: 'custom',
            path: ['esforco'],
            message: 'esforco_desconhecido: fora dos niveis do motor CLAUDE',
          });
        }
        return;
      case 'CODEX':
        if (v.modelo === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['modelo'],
            message: 'modelo_obrigatorio_no_motor: o catalogo do Codex vem da maquina, nao ha padrao',
          });
        }
        return;
      case 'OPENCODE':
        if (v.confirmacaoOpencode !== true) {
          ctx.addIssue({
            code: 'custom',
            path: ['confirmacaoOpencode'],
            message:
              'confirmacao_opencode_obrigatoria: a conversa sai da rede de casa e a sessao gasta credito',
          });
        }
        if (origem === 'mestre') {
          if (v.modelo !== undefined || v.esforco !== undefined) {
            ctx.addIssue({
              code: 'custom',
              path: [v.modelo !== undefined ? 'modelo' : 'esforco'],
              message:
                'modelo_da_mestre_vem_da_config: o modelo e o esforco da mestre do OpenCode sao da tela de Configuracoes',
            });
          }
          return;
        }
        if (v.modelo === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['modelo'],
            message: 'modelo_obrigatorio_no_motor: escolha o modelo do OpenRouter que atende a sessao',
          });
        }
        if (v.esforco !== undefined && !EsforcoOpencodeSchema.safeParse(v.esforco).success) {
          ctx.addIssue({
            code: 'custom',
            path: ['esforco'],
            message: 'esforco_desconhecido: o OpenCode aceita `padrao` ou o nome de um variant do modelo',
          });
        }
        return;
      default:
        motorNaoTratado(v.motor);
    }
  };
}

/**
 * A confirmação do OpenCode (D10, Interface congelada item 5), no molde do
 * `confirmacaoFable`: a conversa sai da rede de casa (OpenRouter e o provedor
 * do modelo, D11) e a sessão gasta crédito da chave. Ausente é `false`, e o
 * `refinarModeloEEsforco` a exige quando o motor é OPENCODE.
 *
 * A mestre que cria sessão OPENCODE pelo canal `mestre.*` NÃO passa por aqui:
 * o dono já autorizou na D4, e o modelo é sempre o padrão da config.
 */
const ConfirmacaoOpencode = z.boolean().default(false);

/** Livre no schema; quem governa por motor é o `refinarModeloEEsforco`. */
const ModeloLivre = z.string().min(1).max(200).optional();
const EsforcoLivre = z.string().min(1).max(40).optional();

export const CriarSessaoSchema = z
  .object({
    maquinaId: z.string().min(1),
    cwd: z.string().min(1).max(500),
    nome: z.string().min(1).max(80).optional(),
    modo: ModoPermissaoSchema.default('acceptEdits'),
    /** Ausente nasce CLAUDE: toda carga que não fala de motor É Claude. */
    motor: MotorSchema,
    modelo: ModeloLivre,
    esforco: EsforcoLivre,
    confirmacaoFable: z.boolean().default(false),
    /** Modo perigoso (dontAsk/bypass) exige ADMIN e esta confirmação (D37). */
    confirmacaoModoPerigoso: z.boolean().default(false),
    confirmacaoOpencode: ConfirmacaoOpencode,
    primeiraMensagem: z.string().max(65_536).optional(),
  })
  .superRefine(refinarModeloEEsforco('sessao'));
export type CriarSessao = z.infer<typeof CriarSessaoSchema>;

/**
 * Continuar, no painel, uma conversa aberta no VS Code.
 *
 * Sem `maquinaId` nem `cwd`: os dois vêm da sessão observada, e deixar o
 * cliente escolher seria deixar o cliente retomar qualquer conversa da
 * máquina. O resto é igual ao criar, porque os portões são os mesmos — modo
 * perigoso exige ADMIN (D37) e Fable exige confirmação.
 */
/**
 * Criação da sessão mestre (F13): rota própria, só ADMIN.
 *
 * `modo` NÃO vem do cliente — o service fixa `auto` — e `confirmacaoMestre` é
 * o gate explícito no molde do Fable: em `auto` os despachos do mestre fluem
 * sem cartão de permissão, e ninguém liga isso sem dizer que sabe o que está
 * ligando. Sem `confirmacaoModoPerigoso` aqui: `auto` não é modo perigoso
 * (D16), e o campo só confundiria.
 */
export const CriarMestreSchema = z
  .object({
    maquinaId: z.string().min(1),
    cwd: z.string().min(1).max(500),
    /** Um mestre por motor: é este campo que diz qual dos dois está nascendo. */
    motor: MotorSchema,
    modelo: ModeloLivre,
    esforco: EsforcoLivre,
    confirmacaoFable: z.boolean().default(false),
    confirmacaoMestre: z.boolean().default(false),
    confirmacaoOpencode: ConfirmacaoOpencode,
    primeiraMensagem: z.string().max(65_536).optional(),
    /**
     * A mestre NASCER em bypass (ADR-0023). Só esse valor existe aqui, e só vale
     * com o bypass total ligado naquela máquina; sem ele, a API recusa com
     * `bypass_total_desligado` em vez de criar uma mestre que pergunta.
     * Opcional e SEM default: ausente é a mestre de sempre, no modo do motor.
     */
    modo: z.literal('bypassPermissions').optional(),
    confirmacaoModoPerigoso: z.boolean().default(false),
  })
  .superRefine(refinarModeloEEsforco('mestre'));
export type CriarMestre = z.infer<typeof CriarMestreSchema>;

/**
 * **Sem `motor`, e não por esquecimento.** Continuar é entrar numa conversa que
 * já existe no disco da máquina — uma sessão que alguém abriu no VS Code, lida
 * pelo tail de JSONL. O Codex não tem JSONL (o histórico dele mora num SQLite
 * do `CODEX_HOME`), e observar sessão de terminal está fora do escopo desta
 * entrega por decisão do dono. Este caminho é 100% Claude por construção, e um
 * campo de motor aqui prometeria uma escolha que não existe.
 *
 * Por isso `modelo` e `esforco` continuam presos aos enums da casa: aqui eles
 * sabem de qual motor são.
 */
export const ContinuarSessaoSchema = z.object({
  modo: ModoPermissaoSchema.default('acceptEdits'),
  modelo: ModeloSchema.optional(),
  esforco: EsforcoSchema.optional(),
  confirmacaoFable: z.boolean().default(false),
  confirmacaoModoPerigoso: z.boolean().default(false),
});
export type ContinuarSessao = z.infer<typeof ContinuarSessaoSchema>;

/**
 * Continuar a conversa NO OUTRO MOTOR: nasce uma sessão nova lá, com as
 * últimas mensagens desta como primeira mensagem (`montarHeranca`), e esta
 * adormece e sai da lista. Um `sessaoId` nunca troca de motor.
 *
 * Sem `maquinaId`/`cwd` no corpo — vêm da sessão antiga, nunca do cliente
 * (mesma razão do `ContinuarSessaoSchema` acima).
 */
export const TrocarMotorSessaoSchema = z
  .object({
    /**
     * O DESTINO, obrigatório e SEM default — a mesma razão do
     * `nivelDePergunta`: com o `.default('CLAUDE')` que o `MotorSchema` tem,
     * um cliente que esquecesse o campo trocaria para um motor escolhido
     * calado. `z.enum(MOTORES)` não tem default: faltou, não valida.
     */
    motor: z.enum(MOTORES),
    modelo: ModeloLivre,
    esforco: EsforcoLivre,
    /**
     * Sem `.default()`: o modo é sempre ESCOLHIDO na troca — `plan`/`dontAsk`
     * não existem no CODEX, então "herdar" o modo calado é impossível, e um
     * padrão esconderia justamente a escolha que a tela precisa apresentar.
     */
    modo: ModoPermissaoSchema,
    confirmacaoFable: z.boolean().default(false),
    confirmacaoModoPerigoso: z.boolean().default(false),
    /** Exigida quando o DESTINO é OPENCODE (D10): a troca é criar uma sessão lá. */
    confirmacaoOpencode: ConfirmacaoOpencode,
  })
  .superRefine(refinarModeloEEsforco('sessao'));
export type TrocarMotorSessao = z.infer<typeof TrocarMotorSessaoSchema>;

export const RenomearSessaoSchema = z.object({ nome: z.string().min(1).max(80) });
export type RenomearSessao = z.infer<typeof RenomearSessaoSchema>;

/** Tirar da lista sem matar, ou devolver. */
export const ArquivarSessaoSchema = z.object({ arquivada: z.boolean() });
export type ArquivarSessao = z.infer<typeof ArquivarSessaoSchema>;

/**
 * Ligar e desligar o aviso desta conversa, para quem pediu.
 *
 * O corpo carrega o estado desejado, e não um "alterna": dois aparelhos
 * tocando o mesmo botão quase junto acabariam em estados trocados, e o
 * segundo desfaria o primeiro sem ninguém pedir.
 */
export const SilenciarSessaoSchema = z.object({ silenciada: z.boolean() });
export type SilenciarSessao = z.infer<typeof SilenciarSessaoSchema>;

/**
 * Recomecar a sessao mestre: encerra a vigente e cria outra na mesma pasta.
 *
 * Um gesto so, e nao dois, por dois motivos. O primeiro e que a pasta E a
 * memoria dela — recomecar noutra pasta seria comecar sem memoria nenhuma, e
 * digitar o caminho a mao na pressa e o jeito mais facil de errar isso. O
 * segundo e a janela: entre encerrar e criar, a frota fica sem mestre, e essa
 * janela deve durar o que o servidor leva, nao o que a pessoa leva.
 */
export const RecomecarMestreSchema = z.object({
  /*
    Com DOIS mestres, "recomeçar o mestre" deixou de ser uma frase completa.
    Sem este campo o servidor escolheria por conta própria qual encerrar — e
    encerrar é o gesto que não tem volta. Ausente nasce CLAUDE, que é o único
    que existia quando a rota foi escrita: o cliente antigo continua acertando.
  */
  motor: MotorSchema,
  confirmacaoMestre: z.boolean().default(false),
  primeiraMensagem: z.string().max(65_536).optional(),
});
export type RecomecarMestre = z.infer<typeof RecomecarMestreSchema>;

/**
 * Quantas sessoes `GET /sessoes` devolve, no maximo.
 *
 * A consulta nao tem cursor: e uma pagina so, e o corte pega as MAIS ANTIGAS,
 * porque a ordem e por atividade decrescente. Por isso o numero e compartilhado
 * em vez de ficar escrito nos dois lados: o painel precisa saber quando esta
 * encostado no teto para avisar que ordenar "mais antigas" ordena as mais
 * antigas DESTA pagina, e nao da conta inteira.
 */
export const TETO_LISTA_SESSOES = 200;

export const ListarSessoesSchema = z.object({
  maquinaId: z.string().optional(),
  cwd: z.string().optional(),
  estado: EstadoSessaoSchema.optional(),
  /**
   * Trazer tambem as arquivadas.
   *
   * Fora por padrao, e do lado do servidor: arquivada que viaja e depois e
   * escondida na tela continua disputando as 200 vagas da consulta, e a
   * primeira coisa que uma lista arquivada faz e crescer.
   */
  incluirArquivadas: z.coerce.boolean().default(false),
});
export type ListarSessoes = z.infer<typeof ListarSessoesSchema>;

/**
 * Leitura do transcript direto na maquina. `ateOffset` vem da resposta
 * anterior: e assim que a tela caminha para tras sem repetir nada.
 */
export const LerTranscriptQuerySchema = z.object({
  ateOffset: z.coerce.number().int().nonnegative().optional(),
  bytes: z.coerce
    .number()
    .int()
    .min(4 * 1024)
    .max(1024 * 1024)
    .default(256 * 1024),
});
export type LerTranscriptQuery = z.infer<typeof LerTranscriptQuerySchema>;

export const SessaoSchema = z.object({
  id: z.string(),
  maquinaId: z.string(),
  maquinaNome: z.string(),
  /**
   * O id que o MOTOR daquela sessão deu para ela.
   *
   * No Claude é o `sessionId` do CLI; no Codex é o `threadId` do app-server.
   * A coluna já se chama `id_no_motor` desde a migration do renome, e a rota
   * devolve a linha do Prisma inteira (`porId` usa `include`, `listar` usa
   * `...s`): o nome novo JÁ viaja na resposta desde então. Este schema estava
   * descrevendo uma resposta que a API não manda mais.
   */
  idNoMotor: z.string(),
  origem: OrigemSessaoSchema,
  estado: EstadoSessaoSchema,
  funcao: FuncaoSessaoSchema.default('NORMAL'),
  cwd: z.string(),
  nome: z.string().nullable(),
  /**
   * Como a máquina chama esta sessão (o `name` do próprio CLI). Campo novo
   * entra opcional com `.default()`: painel antigo segue válido.
   */
  nomeMaquina: z.string().nullable().default(null),
  tituloIa: z.string().nullable(),
  modo: ModoPermissaoSchema.nullable(),
  modelo: z.string().nullable(),
  esforco: EsforcoSchema.nullable().optional(),
  /**
   * O botão "Compactação automática do Remoto" desta conversa (R4). Vale para
   * todo mundo que a vê. Campo novo com `.default(true)`: API anterior não o
   * manda, e ausente é "ligado", que é como a coluna nasce.
   */
  compactacaoAutomatica: z.boolean().default(true),
  entrypoint: z.string().nullable(),
  bridgeSessionId: z.string().nullable(),
  /**
   * A versão do CLI que atendeu esta sessão — QUAL CLI, diz o `motor` da linha.
   *
   * Não confundir com `Maquina.versaoClaude`, que fica com o nome antigo de
   * propósito: lá o campo diz qual Claude Code está instalado naquela máquina,
   * e ganhou uma irmã `versaoCodex` ao lado. Aqui é uma coluna só, porque uma
   * sessão tem um motor só.
   */
  versaoMotor: z.string().nullable(),
  ultimoEventoEm: z.iso.datetime().nullable(),
  /**
   * De qual sessão esta nasceu numa TROCA DE MOTOR. Linhagem para a tela
   * desenhar "continuada de outra conversa" e navegar de volta; nunca lida
   * para escopo. Campo novo com `.default()`: painel antigo segue válido.
   */
  continuadaDeId: z.string().nullable().default(null),
  pedidosPendentes: z.number().int().nonnegative().default(0),
  ultimoSeq: z.number().int().nonnegative().default(0),
  /**
   * Quando a máquina avisou que o último turno terminou. É carimbo do servidor
   * sobre um fato relatado pela máquina, não agregado: quem o calcula por
   * varredura pagaria uma subconsulta por sessão a cada dez segundos de lista.
   */
  ultimoTurnoFimEm: z.iso.datetime().nullable().optional(),
  /** Até que fim de turno esta pessoa já viu esta sessão. */
  turnoFimVisto: z.iso.datetime().nullable().optional(),
  /**
   * Esta pessoa mandou esta conversa parar de avisar.
   *
   * Escolha dela, e não consequência do uso: silenciar não marca como vista, e
   * a conversa segue trabalhando e acumulando novidade — ela só não interrompe.
   */
  silenciada: z.boolean().optional(),
  /**
   * As mestres VIGENTES que vigiam esta conversa (tabela `acompanhamentos_mestre`,
   * com a vigília de uma mestre anterior herdada pela vigente do mesmo motor).
   * Só entra o id de uma mestre que está na lista DESTA pessoa; a lista (e não
   * o detalhe) é quem preenche. Ausente = a API não disse.
   */
  vigiadaPor: z.array(z.string()).optional(),
});
export type Sessao = z.infer<typeof SessaoSchema>;

/** O corpo de `POST /sessoes/:id/compactacao`. */
export const CompactacaoAutomaticaSchema = z.object({ automatica: z.boolean() });
export type CompactacaoAutomatica = z.infer<typeof CompactacaoAutomaticaSchema>;
